import React, { Fragment } from 'react';
import { PropTypes } from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, Divider } from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import { useFormikContext } from 'formik';
import { useMeContext } from '~/modules/me';
import SkillsSelectDropdown from '~/modules/resourcing/common/components/Skills/SkillsSelectDropdown';
import AddUserDefinedSkillDialog from '../AddUserDefinedSkillDialog';
import CompanySkillTitle from '../CompanySkillTitle';
import { useStyles as useHeaderStyles } from '../UserDefinedSkillList';
import MobileSkillRow from './MobileSkillRow';
import MobileSkillCategory from './MobileSkillCategory';
import MobileUserDefinedSkillRow from './MobileUserDefinedSkillRow';

const useStyles = makeStyles(theme => ({
  skillRows: {
    margin: theme.spacing(1, 1.75, 0)
  },
  skillOptions: {
    margin: theme.spacing(0, 3, 2)
  },
  divider: {
    margin: theme.spacing(1.5, -3, 0)
  },
  skillCategory: {
    display: 'flex',
    padding: theme.spacing(0.5, 2),
    backgroundColor: theme.palette.grey[100]
  },
  noSkill: {
    padding: theme.spacing(1, 3)
  },
  assignSkillMessage: {
    paddingTop: theme.spacing(1)
  },
  userDefinedSkillsHeader: {
    padding: theme.spacing(2, 3, 1, 3),
    backgroundColor: theme.palette.grey[50]
  },
  userDefinedSkillRow: {
    margin: theme.spacing(1, 1.75, 0)
  },
  companySkillTitle: {
    margin: theme.spacing(0, 0, 0, 2)
  }
}));

const AddNewUserDefinedSkillDialog = onSkillAdd => props => (
  <AddUserDefinedSkillDialog {...props} onSkillAdd={onSkillAdd} />
);

export const MobileSkillsEditable = ({
  onSkillsSelected,
  onCategoryRemove,
  userDetails,
  maxSkillLevel,
  certificates,
  onSkillAdd,
  skillSetting,
  onUserDefinedSkillRemove,
  onUserDefinedSkillRatingUpdate,
  onUserDefinedSkillsAdd,
  ...rest
}) => {
  const classes = useStyles();
  const headerClasses = useHeaderStyles();
  const {
    featureFlags: { isPsaPraaUserProfileNudgesEnabled }
  } = useMeContext();
  const {
    values: { categories, skills, userDefinedSkills = [] }
  } = useFormikContext();

  const canEditUserDefinedSkill = (
    userDetails?.permittedActions || []
  ).includes('urn:replicon:user-action:edit-skill-assignments');

  const hasSkills = skills.length > 0 || userDefinedSkills.length > 0;
  const canAddUserDefinedSkills =
    canEditUserDefinedSkill && isPsaPraaUserProfileNudgesEnabled;

  return (
    <>
      <Grid container direction="row" className={classes.container}>
        <Grid item xs={12} className={classes.skillOptions}>
          <SkillsSelectDropdown
            value={skills.map(u => ({
              ...u,
              value: u.uri,
              label: u.displayText
            }))}
            onChange={onSkillsSelected}
            onSkillAdd={onSkillAdd}
            classes={classes}
            AddNewCustomSkillDialog={
              canAddUserDefinedSkills
                ? AddNewUserDefinedSkillDialog(onUserDefinedSkillsAdd)
                : undefined
            }
            canAddUserDefinedSkills={canAddUserDefinedSkills}
          />
        </Grid>
        {hasSkills ? (
          <Grid item xs={12}>
            {isPsaPraaUserProfileNudgesEnabled && userDefinedSkills.length > 0 && (
              <>
                <Grid item className={classes.userDefinedSkillsHeader} xs={12}>
                  <div className={headerClasses.title}>
                    <FormattedMessage id="skillInfoCard.userDefinedSkills" />
                  </div>
                  <div className={headerClasses.description}>
                    <FormattedMessage id="skillInfoCard.skillsDescription" />
                  </div>
                </Grid>
                <Grid item className={classes.userDefinedSkillRow} xs={12}>
                  {userDefinedSkills.map((userDefinedSkill, index) => (
                    <div key={userDefinedSkill.displayText}>
                      <MobileUserDefinedSkillRow
                        userDefinedSkill={userDefinedSkill}
                        maxSkillLevel={maxSkillLevel}
                        onUserDefinedSkillRemove={onUserDefinedSkillRemove}
                        onUserDefinedSkillRatingUpdate={
                          onUserDefinedSkillRatingUpdate
                        }
                      />
                      {index < userDefinedSkills.length - 1 && (
                        <Divider
                          variant="fullWidth"
                          className={classes.divider}
                        />
                      )}
                    </div>
                  ))}
                </Grid>
              </>
            )}
            {isPsaPraaUserProfileNudgesEnabled &&
              userDefinedSkills?.length > 0 &&
              skills?.length > 0 && (
                <CompanySkillTitle className={classes.companySkillTitle} />
              )}
            {categories.map(category => (
              <Fragment key={category.uri}>
                <Grid item className={classes.skillCategory} xs={12}>
                  <MobileSkillCategory
                    category={category}
                    onCategoryRemove={onCategoryRemove}
                  />
                </Grid>
                <Grid item className={classes.skillRows} xs={12}>
                  {skills
                    .filter(skill => skill.category.uri === category.uri)
                    .map(skill => (
                      <div key={skill.uri}>
                        <MobileSkillRow
                          skill={skill}
                          certificates={certificates}
                          userDetails={userDetails}
                          skillSetting={skillSetting}
                          maxSkillLevel={maxSkillLevel}
                          {...rest}
                        />
                        <Divider
                          variant="fullWidth"
                          className={classes.divider}
                        />
                      </div>
                    ))}
                </Grid>
              </Fragment>
            ))}
          </Grid>
        ) : (
          <>
            <div className={classes.noSkill}>
              <div>
                <FormattedMessage id="skillInfoCard.noSkillsAssignedMessage" />
              </div>
              <div className={classes.assignSkillMessage}>
                <FormattedMessage id="skillInfoCard.assignSkillMessage" />
              </div>
            </div>
          </>
        )}
      </Grid>
    </>
  );
};

MobileSkillsEditable.propTypes = {
  onSkillsSelected: PropTypes.func,
  onSkillRemove: PropTypes.func,
  onCategoryRemove: PropTypes.func,
  certificates: PropTypes.array,
  userDetails: PropTypes.object,
  skillSetting: PropTypes.array,
  onSkillAdd: PropTypes.func,
  maxSkillLevel: PropTypes.number,
  onUserDefinedSkillRemove: PropTypes.func,
  onUserDefinedSkillRatingUpdate: PropTypes.func,
  onUserDefinedSkillsAdd: PropTypes.func
};

export default MobileSkillsEditable;
